import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import API_BASE from '../config';

const RATIOS = ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '2:1'];

export default function TextToImage({ onSave }) {
  const { token } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [ratio, setRatio] = useState('1:1');
  const [resolution, setResolution] = useState('1k');
  const [count, setCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  // images 가 undefined 가 되지 않도록 항상 배열로 보정
  const imgs = Array.isArray(images) ? images : [];

  const generate = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setError(null);
    setImages([]);
    try {
      const res = await fetch(`${API_BASE}/api/t2i`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ prompt, ratio, resolution, n: count }),
      });
      const data = await res.json();
      if (data && data.success) {
        // {images: [...]} 또는 {image: '...'} 두 형식 모두 처리
        let result;
        if (Array.isArray(data.images)) {
          result = data.images;
        } else if (data.image) {
          result = [data.image];
        } else {
          result = [];
        }
        setImages(result);
        result.forEach(url => onSave({ type: 't2i', prompt, url }));
      } else {
        setError((data && data.error) || '알 수 없는 오류');
      }
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const download = (url, idx) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = `grok-t2i-${Date.now()}-${idx + 1}.png`;
    a.click();
  };

  return (
    <>
      <div className="panel">
        <div className="panel-header">
          <div className="panel-icon green">🖼</div>
          <div className="panel-title-group">
            <div className="panel-title">텍스트 → 이미지</div>
            <div className="panel-sub">텍스트 프롬프트로 이미지 생성</div>
          </div>
        </div>

        <div className="panel-body">
          {/* ── 왼쪽: 입력 ── */}
          <div className="form-col">
            <div className="form-group">
              <label>프롬프트</label>
              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder="네온 불빛이 빛나는 사이버펑크 도시, 비 오는 밤..."
                rows={5}
              />
            </div>

            <div className="form-group">
              <label>생성 수량</label>
              <div className="count-selector">
                {[1, 2, 3, 4].map(n => (
                  <button
                    key={n}
                    className="count-btn"
                    onClick={() => setCount(n)}
                    style={{
                      border: count === n ? '2px solid var(--accent)' : '1px solid var(--border)',
                      background: count === n ? 'rgba(0,255,136,0.12)' : 'var(--bg3)',
                      color: count === n ? 'var(--accent)' : 'var(--text2)',
                    }}
                  >
                    {n}장
                  </button>
                ))}
              </div>
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label>해상도</label>
                <select value={resolution} onChange={e => setResolution(e.target.value)}>
                  <option value="1k">1K (1024px)</option>
                  <option value="2k">2K (2048px) 고화질</option>
                </select>
              </div>
              <div className="form-group">
                <label>화면 비율</label>
                <div className="ratio-grid">
                  {RATIOS.map(r => (
                    <button
                      key={r}
                      className="ratio-btn"
                      onClick={() => setRatio(r)}
                      style={{
                        border: ratio === r ? '2px solid var(--accent)' : '1px solid var(--border)',
                        background: ratio === r ? 'rgba(0,255,136,0.12)' : 'var(--bg3)',
                        color: ratio === r ? 'var(--accent)' : 'var(--text2)',
                        fontWeight: ratio === r ? '700' : '400',
                      }}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button
              className="btn btn-green"
              onClick={generate}
              disabled={loading || !prompt.trim()}
            >
              {loading ? '⏳ 생성 중...' : `✦ 이미지 생성${count > 1 ? ` (${count}장)` : ''}`}
            </button>

            {error && <div className="error-box">⚠ {error}</div>}
          </div>

          {/* ── 오른쪽: 결과 ── */}
          <div className="output-area">
            <span className="output-label">결과</span>
            <div className="output-box" style={{ minHeight: '260px' }}>
              {loading ? (
                <div className="loading">
                  <div className="loading-bar">
                    <div className="loading-bar-fill" style={{ background: 'var(--accent)' }} />
                  </div>
                  <span className="loading-text">이미지 생성 중...</span>
                </div>
              ) : imgs.length > 1 ? (
                <div className="multi-image-grid">
                  {imgs.map((url, i) => (
                    <div key={i} className="multi-image-item">
                      <img src={url} alt={`result-${i}`} onClick={() => setLightbox(url)} />
                    </div>
                  ))}
                </div>
              ) : imgs.length === 1 ? (
                <img
                  src={imgs[0]}
                  alt="generated"
                  className="output-image"
                  onClick={() => setLightbox(imgs[0])}
                />
              ) : (
                <div className="output-placeholder">
                  <span className="output-placeholder-icon">🖼</span>
                  <span className="output-placeholder-text">이미지가 여기 표시됩니다</span>
                </div>
              )}
            </div>

            {imgs.length > 0 && (
              <div className="download-bar">
                {imgs.map((url, i) => (
                  <button key={i} className="btn-download" onClick={() => download(url, i)}>
                    ↓ {imgs.length > 1 ? `#${i + 1} ` : ''}PNG
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {lightbox && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <button className="lightbox-close" onClick={() => setLightbox(null)}>✕</button>
          <img src={lightbox} alt="lightbox" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </>
  );
}
